import { AxiosResponse } from 'axios';

import { Customer } from 'types/Customer';
import { objectKeysToCamelCase, objectKeysToSnakeCase } from 'utils/common';
import axiosWithToken from './axios-with-token';
import { WatchListFilters, removeNullAndFormatFilters, WatchedShipmentRequest } from './watchList';

interface ServerCustomer {
  id: string;
  code: string;
  count: number;
  created_by: string;
}

interface CustomersFetchResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ServerCustomer[];
}

interface CustomerFilters {
  pageSize?: number;
  q?: string;
}

type CustomerCreateResponse = ServerCustomer;

type CustomerUpdateResponse = ServerCustomer;

export enum QueryKeys {
  CUSTOMERS = 'customers',
  WATCHLIST_CUSTOMERS = 'watchlistCustomers',
  SHIPMENTS_CUSTOMERS = 'shipmentsCustomers',
}

export async function fetchCustomers({
  signal,
  q,
}: {
  signal?: AbortSignal;
  q?: string;
}): Promise<Customer[]> {
  const params: CustomerFilters = {};

  if (q) {
    params.q = q;
  }

  const response: AxiosResponse<CustomersFetchResponse> = await axiosWithToken.get(
    '/core/api/v1/customers/',
    { params: objectKeysToSnakeCase(params), signal }
  );

  const customers: Customer[] = objectKeysToCamelCase(response.data);
  return customers;
}

export async function fetchWatchlistCustomers(
  filters: WatchListFilters,
  searchCode: string,
  signal?: AbortSignal,
  params: { pageSize: number; page: number } = { pageSize: 1000, page: 1 }
) {
  const response: AxiosResponse<CustomersFetchResponse> = await axiosWithToken.post(
    '/core/api/v1/customers/list/watchlist',
    objectKeysToSnakeCase(
      { filters: removeNullAndFormatFilters({ ...filters }), searchCode },
      true
    ),
    {
      params,
      signal,
    }
  );

  const customers: Customer[] = objectKeysToCamelCase(response.data.results);
  return customers;
}

export async function fetchShipmentsCustomers(
  filters: WatchedShipmentRequest,
  searchCode: string,
  signal?: AbortSignal,
  params: { pageSize: number; page: number } = { pageSize: 1000, page: 1 }
) {
  const response: AxiosResponse<CustomersFetchResponse> = await axiosWithToken.post(
    '/core/api/v1/customers/list/shipments',
    objectKeysToSnakeCase({ filters, searchCode }, true),
    {
      params,
      signal,
    }
  );

  const customers: Customer[] = objectKeysToCamelCase(response.data.results);
  return customers;
}

export async function createCustomer(code: string): Promise<Customer> {
  const response: AxiosResponse<CustomerCreateResponse> = await axiosWithToken.post(
    '/core/api/v1/customers/',
    { code }
  );

  const customer: Customer = objectKeysToCamelCase(response.data);
  return customer;
}

export async function deleteCustomer(id: string) {
  await axiosWithToken.delete(`/core/api/v1/customers/${id}`);
}

export async function updateCustomer(customer: Customer) {
  const response: AxiosResponse<CustomerUpdateResponse> = await axiosWithToken.put(
    `/core/api/v1/customers/${customer.id}`,
    { ...customer }
  );

  const updatedCustomer: Customer = objectKeysToCamelCase(response.data);
  return updatedCustomer;
}

export async function getCustomer(id: string) {
  const response = await axiosWithToken.get<ServerCustomer>(`/core/api/v1/customers/${id}`);
  const customer: Customer = objectKeysToCamelCase(response.data);
  return customer;
}
